import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRole } from '../context/roleContext';
import {
  faPlus,
  faRefresh,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { AdminGuard } from '../components/RoleGuards';
import HsnTable from '../components/hsnCodes/HsnTable';
import HsnForm from '../components/hsnCodes/HsnForm';
import ActionToolbar from '../components/common/ActionToolbar';
import useHsnCodes from '../hooks/useHsnCodes';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useErrorDialog } from '../context/errorDialogContext';
import taxesService from '../services/taxesService';
import { useTranslation } from 'react-i18next';

/**
 * HsnCodesPage
 * - Lists HSN codes and provides create/update/delete operations.
 * - Fetches tax list on mount to provide tax options in the form.
 */
const HsnCodesPage = () => {
  const { t } = useTranslation();
  const {
    hsnCodes,
    loading,
    error,
    totalPages,
    totalCount,
    fetchHsnCodes,
    createHsnCode,
    updateHsnCode,
    deleteHsnCode,
    bulkDeleteHsnCodes,
    clearError,
  } = useHsnCodes();

  const PAGE_LIMIT = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const { checkDeleteAccess } = useRole();

  const { showError } = useErrorDialog();

  React.useEffect(() => {
    if (error) {
      showError(error, t('hsnCodes.page.alerts.errorTitle'));
      if (typeof clearError === 'function') clearError();
    }
  }, [error, showError, clearError]);

  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Selection state
  const [selectedHsnCodes, setSelectedHsnCodes] = useState(new Set());

  // Taxes state - fetch once when page loads
  const [taxesList, setTaxesList] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingHsnCode, setEditingHsnCode] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const displayedHsnCodes = useMemo(() => Array.isArray(hsnCodes) ? hsnCodes : [], [hsnCodes]);

  const loadHsnCodes = useCallback((isRefresh = false, search = committedSearch) => {
    fetchHsnCodes({ skip: (currentPage - 1) * PAGE_LIMIT, limit: PAGE_LIMIT, ...(search ? { search } : {}) }, { force: isRefresh });
  }, [fetchHsnCodes, currentPage, committedSearch]);

  // Fetch HSN codes on mount and when page changes
  useEffect(() => {
    loadHsnCodes();
  }, [loadHsnCodes]);

  // Fetch taxes on page load (only once)
  useEffect(() => {
    const fetchTaxes = async () => {
      setTaxesLoading(true);
      try {
        const res = await taxesService.getAllTaxes({ limit: 95 });
        if (res.success) {
          const data = res.data;
          let items = [];
          if (data) {
            if (Array.isArray(data)) items = data;
            else if (Array.isArray(data.taxes)) items = data.taxes;
            else if (Array.isArray(data.items)) items = data.items;
          }
          setTaxesList(items);
        } else {
          setTaxesList([]);
        }
      } catch (err) {
        setTaxesList([]);
      } finally {
        setTaxesLoading(false);
      }
    };

    fetchTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  /** Refresh HSN list by forcing a re-fetch from the server */
  const handleRefresh = useCallback(() => {
    loadHsnCodes(true);
  }, [loadHsnCodes]);

  /** Debounced search — updates query param after 400 ms */
  const handleSearch = useCallback((searchTerm) => {
    setSearchQuery(searchTerm);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
  }, []);

  /** Toggle selection of a single HSN code */
  const handleSelectHsnCode = useCallback((hsnCodeId, checked) => {
    setSelectedHsnCodes(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(hsnCodeId);
      } else {
        newSet.delete(hsnCodeId);
      }
      return newSet;
    });
  }, []);

  /** Select or clear all visible HSN codes */
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedHsnCodes(new Set(displayedHsnCodes.map(hsn => hsn.id)));
    } else {
      setSelectedHsnCodes(new Set());
    }
  }, [displayedHsnCodes]);

  /** Open empty form to create a new HSN code */
  const handleAddHsnCode = useCallback(() => {
    setEditingHsnCode(null);
    setShowForm(true);
  }, []);

  /** Open form prefilled for editing a specific HSN code */
  const handleEditHsnCode = useCallback((hsnCode) => {
    setEditingHsnCode(hsnCode);
    setShowForm(true);
  }, []);

  /** Prompt to confirm deletion of a single HSN code */
  const handleDeleteHsnCode = useCallback((hsnCode) => {
    setDeleteTarget(hsnCode);
    setShowDeleteConfirm(true);
  }, []);

  /** Prompt to confirm bulk deletion of selected HSN codes */
  const handleBulkDelete = useCallback(() => {
    if (selectedHsnCodes.size === 0) return;
    setDeleteTarget(Array.from(selectedHsnCodes));
    setShowDeleteConfirm(true);
  }, [selectedHsnCodes]);

  const handleFormSubmit = useCallback(async (hsnData) => {
    setFormLoading(true);

    try {
      let result;
      if (editingHsnCode) {
        result = await updateHsnCode(editingHsnCode.id, hsnData);
      } else {
        result = await createHsnCode(hsnData);
      }

      if (result.success) {
        setShowForm(false);
        setEditingHsnCode(null);
      }
      // return the result so the caller (form) can react to backend validation messages
      return result;
    } finally {
      setFormLoading(false);
    }
  }, [editingHsnCode, createHsnCode, updateHsnCode]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);

    try {
      let result;
      if (Array.isArray(deleteTarget)) {
        // Bulk delete
        result = await bulkDeleteHsnCodes(deleteTarget);
        if (result.success) {
          setSelectedHsnCodes(new Set());
        }
      } else {
        // Single delete
        result = await deleteHsnCode(deleteTarget.id);
      }

      if (result.success) {
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, deleteHsnCode, bulkDeleteHsnCodes]);

  // Define filters for ActionToolbar
  const toolbarFilters = useMemo(() => [], []);

  // Define actions for ActionToolbar
  const toolbarActions = useMemo(() => {
    const actions = [
      {
        key: 'refresh',
        icon: faRefresh,
        onClick: handleRefresh,
        disabled: loading,
        variant: 'secondary',
        tooltip: t('common.buttons.refresh'),
        spinner: loading,
        ariaLabel: t('common.buttons.refresh'),
      },
    ];

    // Bulk delete button (only show if items are selected and user has delete access)
    if (selectedHsnCodes.size > 0 && checkDeleteAccess()) {
      actions.push({
        key: 'bulk-delete',
        icon: faTrash,
        label: t('hsnCodes.actions.buttons.bulkDelete'),
        onClick: handleBulkDelete,
        disabled: loading,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    // Add HSN code button
    actions.push({
      key: 'add-hsn-code',
      icon: faPlus,
      label: t('hsnCodes.actions.buttons.add'),
      onClick: handleAddHsnCode,
      disabled: loading,
      variant: 'primary',
      showLabelBreakpoint: 'sm',
    });

    return actions;
  }, [
    loading,
    selectedHsnCodes.size,
    handleRefresh,
    handleBulkDelete,
    handleAddHsnCode,
    checkDeleteAccess,
  ]);

  return (
    <AdminGuard
      fallback={
        <div className="p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('hsnCodes.page.accessDenied.title')}
            </h2>
            <p className="text-gray-600">{t('hsnCodes.page.accessDenied.message')}</p>
          </div>
        </div>
      }
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('hsnCodes.page.header.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('hsnCodes.page.header.subtitle')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {selectedHsnCodes.size > 0 && (
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-500">
                  {t('hsnCodes.page.stats.selected')}
                </h3>
                <div className="text-2xl font-bold text-green-600">{selectedHsnCodes.size}</div>
              </div>
            </div>
          )}
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder={t('hsnCodes.actions.searchPlaceholder')}
          filters={toolbarFilters}
          actions={toolbarActions}
          className="mb-6"
        />

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <HsnTable
            hsnCodes={displayedHsnCodes}
            loading={loading}
            selectedHsnCodes={selectedHsnCodes}
            onSelectHsnCode={handleSelectHsnCode}
            onSelectAll={handleSelectAll}
            onEditHsnCode={handleEditHsnCode}
            onDeleteHsnCode={handleDeleteHsnCode}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {currentPage} of {totalPages} — {totalCount} total
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#9ca3af' }}>...</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCurrentPage(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* HSN Form Modal */}
        <HsnForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingHsnCode(null);
          }}
          onSubmit={handleFormSubmit}
          hsnCode={editingHsnCode}
          loading={formLoading}
          taxesList={taxesList}
          taxesLoading={taxesLoading}
          existingHsnCodes={hsnCodes}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title={Array.isArray(deleteTarget) ? t('hsnCodes.confirm.deleteBulk.title') : t('hsnCodes.confirm.deleteSingle.title')}
          message={Array.isArray(deleteTarget)
            ? (deleteTarget.length === 1
              ? t('hsnCodes.confirm.deleteBulk.messageSingle')
              : t('hsnCodes.confirm.deleteBulk.messageMultiple', { count: deleteTarget.length }))
            : t('hsnCodes.confirm.deleteSingle.message')}
          confirmLabel={Array.isArray(deleteTarget) ? t('hsnCodes.confirm.deleteBulk.confirmLabel') : t('hsnCodes.confirm.deleteSingle.confirmLabel')}
          loadingLabel={Array.isArray(deleteTarget) ? t('hsnCodes.confirm.deleteBulk.loadingLabel') : t('hsnCodes.confirm.deleteSingle.loadingLabel')}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
          }}
          confirmVariant="danger"
          loading={deleteLoading}
        />
      </div>
    </AdminGuard>
  );
};

export default HsnCodesPage;